import {
  Component,
  Input,
  Output,
  ElementRef,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  OnInit,
  OnDestroy,
  ViewChild,
  AfterViewChecked,
  NgZone
} from '@angular/core';
import { from, fromEvent, Subject } from 'rxjs';
import { debounceTime, filter, takeUntil } from 'rxjs/operators';
import * as PDFJS from 'pdfjs-dist';
import * as PDFJSViewer from 'pdfjs-dist/web/pdf_viewer.mjs';

import { GlobalWorkerOptions, VerbosityLevel, getDocument } from 'pdfjs-dist';
import type { PDFSource, PDFProgressData, PDFDocumentProxy, PDFDocumentLoadingTask, ZoomScale } from './typings';

function createEventBus(pdfJsViewer: any, destroy$: Subject<void>) {
  const globalEventBus = new pdfJsViewer.EventBus();
  attachDOMEventsToEventBus(globalEventBus, destroy$);
  return globalEventBus;
}

function attachDOMEventsToEventBus(eventBus: any, destroy$: Subject<void>) {
  fromEvent(eventBus, 'documentload')
    .pipe(takeUntil(destroy$))
    .subscribe(() => {
      const event = document.createEvent('CustomEvent');
      event.initCustomEvent('documentload', true, true, {});
      window.dispatchEvent(event);
    });
  fromEvent(eventBus, 'pagerendered')
    .pipe(takeUntil(destroy$))
    .subscribe((evt: any) => {
      const { pageNumber, cssTransform, source } = evt;
      const event = document.createEvent('CustomEvent');
      event.initCustomEvent('pagerendered', true, true, {
        pageNumber,
        cssTransform,
      });
      source.div.dispatchEvent(event);
    });
  fromEvent(eventBus, 'textlayerrendered')
    .pipe(takeUntil(destroy$))
    .subscribe((evt: any) => {
      const { pageNumber, source } = evt;
      const event = document.createEvent('CustomEvent');
      event.initCustomEvent('textlayerrendered', true, true, { pageNumber });
      source.textLayerDiv?.dispatchEvent(event);
    });
  fromEvent(eventBus, 'pagechanging')
    .pipe(takeUntil(destroy$))
    .subscribe((evt: any) => {
      const { pageNumber, source } = evt;
      const event = document.createEvent('UIEvents');
      event.initEvent('pagechanging', true, true);
      /* tslint:disable:no-string-literal */
      (event as any)['pageNumber'] = pageNumber;
      source.container.dispatchEvent(event);
    });
  fromEvent(eventBus, 'pagesinit')
    .pipe(takeUntil(destroy$))
    .subscribe((evt: any) => {
      const { source } = evt;
      const event = document.createEvent('CustomEvent');
      event.initCustomEvent('pagesinit', true, true, null);
      source.container.dispatchEvent(event);
    });
  fromEvent(eventBus, 'pagesloaded')
    .pipe(takeUntil(destroy$))
    .subscribe((evt: any) => {
      const { pagesCount, source } = evt;
      const event = document.createEvent('CustomEvent');
      event.initCustomEvent('pagesloaded', true, true, { pagesCount });
      source.container.dispatchEvent(event);
    });
  fromEvent(eventBus, 'scalechange')
    .pipe(takeUntil(destroy$))
    .subscribe((evt: any) => {
      const { scale, presetValue, source } = evt;
      const event = document.createEvent('UIEvents');
      event.initEvent('scalechange', true, true);
      /* tslint:disable:no-string-literal */
      (event as any)['scale'] = scale;
      /* tslint:disable:no-string-literal */
      (event as any)['presetValue'] = presetValue;
      source.container.dispatchEvent(event);
    });
  fromEvent(eventBus, 'updateviewarea')
    .pipe(takeUntil(destroy$))
    .subscribe((evt: any) => {
      const { location, source } = evt;
      const event = document.createEvent('UIEvents');
      event.initEvent('updateviewarea', true, true);
      (event as any)['location'] = location;
      source.container.dispatchEvent(event);
    });
  fromEvent(eventBus, 'find')
    .pipe(takeUntil(destroy$))
    .subscribe((evt: any) => {
      const { source, type, query, phraseSearch, caseSensitive, highlightAll, findPrevious } = evt;
      if (source === window) {
        return; // event comes from FirefoxCom, no need to replicate
      }
      const event = document.createEvent('CustomEvent');
      event.initCustomEvent('find' + type, true, true, {
        query,
        phraseSearch,
        caseSensitive,
        highlightAll,
        findPrevious,
      });
      window.dispatchEvent(event);
    });
  fromEvent(eventBus, 'attachmentsloaded')
    .pipe(takeUntil(destroy$))
    .subscribe((evt: any) => {
      const { attachmentsCount, source } = evt;
      const event = document.createEvent('CustomEvent');
      event.initCustomEvent('attachmentsloaded', true, true, {
        attachmentsCount,
      });
      source.container.dispatchEvent(event);
    });
  fromEvent(eventBus, 'sidebarviewchanged')
    .pipe(takeUntil(destroy$))
    .subscribe((evt: any) => {
      const { view, source } = evt;
      const event = document.createEvent('CustomEvent');
      event.initCustomEvent('sidebarviewchanged', true, true, { view });
      source.outerContainer.dispatchEvent(event);
    });
  fromEvent(eventBus, 'pagemode')
    .pipe(takeUntil(destroy$))
    .subscribe((evt: any) => {
      const { mode, source } = evt;
      const event = document.createEvent('CustomEvent');
      event.initCustomEvent('pagemode', true, true, { mode });
      source.pdfViewer.container.dispatchEvent(event);
    });
  fromEvent(eventBus, 'namedaction')
    .pipe(takeUntil(destroy$))
    .subscribe((evt: any) => {
      const { action, source } = evt;
      const event = document.createEvent('CustomEvent');
      event.initCustomEvent('namedaction', true, true, { action });
      source.pdfViewer.container.dispatchEvent(event);
    });
  fromEvent(eventBus, 'presentationmodechanged')
    .pipe(takeUntil(destroy$))
    .subscribe((evt: any) => {
      const { active, switchInProgress } = evt;
      const event = document.createEvent('CustomEvent');
      event.initCustomEvent('presentationmodechanged', true, true, {
        active,
        switchInProgress,
      });
      window.dispatchEvent(event);
    });
  fromEvent(eventBus, 'outlineloaded')
    .pipe(takeUntil(destroy$))
    .subscribe((evt: any) => {
      const { outlineCount, source } = evt;
      const event = document.createEvent('CustomEvent');
      event.initCustomEvent('outlineloaded', true, true, { outlineCount });
      source.container.dispatchEvent(event);
    });
}

function assign(obj: any, prop: string, value: any) {
  obj[prop] = value;
}
function isSSR() {
  return typeof window === 'undefined';
}

if (!isSSR()) {
  assign(PDFJS, 'verbosity', VerbosityLevel.INFOS);
}

// Polyfill Promise.withResolvers if needed
if (typeof (Promise as any).withResolvers === 'undefined' && typeof window !== 'undefined') {
  (window as any).Promise.withResolvers = () => {
    let resolve: any;
    let reject: any;
    const promise = new Promise((res, rej) => {
      resolve = res;
      reject = rej;
    });
    return { promise, resolve, reject };
  };
}

export enum RenderTextMode {
  DISABLED,
  ENABLED,
  ENHANCED
}

@Component({
  selector: 'pdf-viewer',
  template: `
    <div #pdfViewerContainer class="ng2-pdf-viewer-container">
      <div class="pdfViewer"></div>
    </div>
  `,
  styleUrls: ['./pdf-viewer-local.component.scss']
})
export class PdfViewerComponent implements OnChanges, OnInit, OnDestroy, AfterViewChecked {
  static CSS_UNITS = 96.0 / 72.0;
  static BORDER_WIDTH = 9;

  @ViewChild('pdfViewerContainer') pdfViewerContainer!: ElementRef<HTMLDivElement>;

  public eventBus!: PDFJSViewer.EventBus;
  public pdfLinkService!: PDFJSViewer.PDFLinkService;
  public pdfFindController!: PDFJSViewer.PDFFindController;
  public pdfViewer!: PDFJSViewer.PDFViewer | PDFJSViewer.PDFSinglePageViewer;

  private isVisible = false;

  private _cMapsUrl =
    typeof PDFJS !== 'undefined'
      ? `https://unpkg.com/pdfjs-dist@${(PDFJS as any).version}/cmaps/`
      : null;
  private _imageResourcesPath =
    typeof PDFJS !== 'undefined'
      ? `https://unpkg.com/pdfjs-dist@${(PDFJS as any).version}/web/images/`
      : undefined;
  private _renderText = true;
  private _renderTextMode: RenderTextMode = RenderTextMode.ENABLED;
  private _stickToPage = false;
  private _originalSize = true;
  private _pdf: PDFDocumentProxy | undefined;
  private _page = 1;
  private _zoom = 1;
  private _zoomScale: ZoomScale = 'page-width';
  private _rotation = 0;
  private _showAll = true;
  private _canAutoResize = true;
  private _fitToPage = false;
  private _externalLinkTarget = 'blank';
  private _showBorders = false;
  private lastLoaded!: string | Uint8Array | PDFSource | null;
  private _latestScrolledPage!: number;

  private pageScrollTimeout: number | null = null;
  private isInitialized = false;
  private loadingTask?: PDFDocumentLoadingTask | null;
  private destroy$ = new Subject<void>();

  @Output('after-load-complete') afterLoadComplete = new EventEmitter<PDFDocumentProxy>();
  @Output('page-rendered') pageRendered = new EventEmitter<CustomEvent>();
  @Output('pages-initialized') pageInitialized = new EventEmitter<CustomEvent>();
  @Output('text-layer-rendered') textLayerRendered = new EventEmitter<CustomEvent>();
  @Output('error') onError = new EventEmitter<any>();
  @Output('on-progress') onProgress = new EventEmitter<PDFProgressData>();
  @Output() pageChange: EventEmitter<number> = new EventEmitter<number>(true);
  @Input() src?: string | Uint8Array | PDFSource;

  @Input('c-maps-url')
  set cMapsUrl(cMapsUrl: string) {
    this._cMapsUrl = cMapsUrl;
  }

  @Input('page')
  set page(_page: number | string | any) {
    _page = parseInt(_page, 10) || 1;
    const originalPage = _page;

    if (this._pdf) {
      _page = this.getValidPageNumber(_page);
    }

    this._page = _page;
    if (originalPage !== _page) {
      this.pageChange.emit(_page);
    }
  }

  @Input('render-text')
  set renderText(renderText: boolean) {
    this._renderText = renderText;
  }

  @Input('render-text-mode')
  set renderTextMode(renderTextMode: RenderTextMode) {
    this._renderTextMode = renderTextMode;
  }

  @Input('original-size')
  set originalSize(originalSize: boolean) {
    this._originalSize = originalSize;
  }

  @Input('show-all')
  set showAll(value: boolean) {
    this._showAll = value;
  }

  @Input('stick-to-page')
  set stickToPage(value: boolean) {
    this._stickToPage = value;
  }

  @Input('zoom')
  set zoom(value: number) {
    if (value <= 0) {
      return;
    }
    this._zoom = value;
  }

  get zoom() {
    return this._zoom;
  }

  @Input('zoom-scale')
  set zoomScale(value: ZoomScale) {
    this._zoomScale = value;
  }

  get zoomScale() {
    return this._zoomScale;
  }

  @Input('rotation')
  set rotation(value: number) {
    if (!(typeof value === 'number' && value % 90 === 0)) {
      console.warn('Invalid pages rotation angle.');
      return;
    }
    this._rotation = value;
  }

  @Input('external-link-target')
  set externalLinkTarget(value: string) {
    this._externalLinkTarget = value;
  }

  @Input('autoresize')
  set autoresize(value: boolean) {
    this._canAutoResize = Boolean(value);
  }

  @Input('fit-to-page')
  set fitToPage(value: boolean) {
    this._fitToPage = Boolean(value);
  }

  @Input('show-borders')
  set showBorders(value: boolean) {
    this._showBorders = Boolean(value);
  }

  static getLinkTarget(type: string) {
    switch (type) {
      case 'blank':
        return PDFJSViewer.LinkTarget.BLANK;
      case 'none':
        return PDFJSViewer.LinkTarget.NONE;
      case 'self':
        return PDFJSViewer.LinkTarget.SELF;
      case 'parent':
        return PDFJSViewer.LinkTarget.PARENT;
      case 'top':
        return PDFJSViewer.LinkTarget.TOP;
    }
    return null;
  }

  constructor(private element: ElementRef<HTMLElement>, private ngZone: NgZone) {
    if (isSSR()) {
      return;
    }
    let pdfWorkerSrc: string;
    const pdfJsVersion = (PDFJS as any).version;
    const versionSpecificPdfWorkerUrl = (window as any)[`pdfWorkerSrc${pdfJsVersion}`];
    if (versionSpecificPdfWorkerUrl) {
      pdfWorkerSrc = versionSpecificPdfWorkerUrl;
    } else if (
      window.hasOwnProperty('pdfWorkerSrc') &&
      typeof (window as any).pdfWorkerSrc === 'string' &&
      (window as any).pdfWorkerSrc
    ) {
      pdfWorkerSrc = (window as any).pdfWorkerSrc;
    } else {
      pdfWorkerSrc = `assets/js/pdf.worker.min.js`;
    }
    assign(GlobalWorkerOptions, 'workerSrc', pdfWorkerSrc);
  }

  ngAfterViewChecked() {
    if (this.isInitialized) {
      return;
    }
    const offset = this.pdfViewerContainer.nativeElement.offsetParent;
    if (this.isVisible === true && offset == null) {
      this.isVisible = false;
      return;
    }
    if (this.isVisible === false && offset != null) {
      this.isVisible = true;
      setTimeout(() => {
        this.initialize();
        this.ngOnChanges({ src: this.src as any });
      });
    }
  }

  ngOnInit() {
    this.initialize();
    this.setupResizeListener();
  }

  ngOnDestroy() {
    this.clear();
    this.destroy$.next();
    this.loadingTask = null;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (isSSR() || !this.isVisible) {
      return;
    }
    if ('src' in changes) {
      this.loadPDF();
    } else if (this._pdf) {
      if ('renderText' in changes || 'showAll' in changes) {
        this.setupViewer();
        this.resetPdfDocument();
      }
      if ('page' in changes) {
        const { page } = changes;
        if (page.currentValue === this._latestScrolledPage) {
          return;
        }
        this.pdfViewer.scrollPageIntoView({ pageNumber: this._page });
      }
      this.update();
    }
  }

  updateSize() {
    if (!this._pdf) return;
    from(this._pdf.getPage(this.pdfViewer.currentPageNumber))
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (page) => {
          const rotation = this._rotation + page.rotate;
          const viewportWidth =
            page.getViewport({
              scale: this._zoom,
              rotation
            }).width * PdfViewerComponent.CSS_UNITS;
          let scale = this._zoom;
          let stickToPage = true;
          if (
            !this._originalSize ||
            (this._fitToPage &&
              viewportWidth > this.pdfViewerContainer.nativeElement.clientWidth)
          ) {
            const viewPort = page.getViewport({ scale: 1, rotation });
            scale = this.getScale(viewPort.width, viewPort.height);
            stickToPage = !this._stickToPage;
          }
          this.pdfViewer.currentScale = scale;
          if (stickToPage) {
            this.pdfViewer.scrollPageIntoView({
              pageNumber: page.pageNumber,
              ignoreDestinationZoom: true
            });
          }
        }
      });
  }

  clear() {
    if (this.loadingTask && !this.loadingTask.destroyed) {
      this.loadingTask.destroy();
    }
    if (this._pdf) {
      this._latestScrolledPage = 0;
      this._pdf.destroy();
      this._pdf = undefined;
    }
    this.pdfViewer && this.pdfViewer.setDocument(null);
    this.pdfLinkService && this.pdfLinkService.setDocument(null, null);
    this.pdfFindController && this.pdfFindController.setDocument(null);
  }

  getPDFLinkServiceConfig() {
    const linkTarget = PdfViewerComponent.getLinkTarget(this._externalLinkTarget);
    if (linkTarget) {
      return { externalLinkTarget: linkTarget };
    }
    return {};
  }

  initEventBus() {
    this.eventBus = createEventBus(PDFJSViewer, this.destroy$);
    fromEvent(this.eventBus, 'pagerendered')
      .pipe(takeUntil(this.destroy$))
      .subscribe((event: any) => {
        this.pageRendered.emit(event);
      });
    fromEvent(this.eventBus, 'pagesinit')
      .pipe(takeUntil(this.destroy$))
      .subscribe((event: any) => {
        this.pageInitialized.emit(event);
      });
    fromEvent(this.eventBus, 'pagechanging')
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ pageNumber }: any) => {
        if (this.pageScrollTimeout) {
          clearTimeout(this.pageScrollTimeout);
        }
        this.pageScrollTimeout = window.setTimeout(() => {
          this._latestScrolledPage = pageNumber;
          this.pageChange.emit(pageNumber);
        }, 100);
      });
    fromEvent(this.eventBus, 'textlayerrendered')
      .pipe(takeUntil(this.destroy$))
      .subscribe((event: any) => {
        this.textLayerRendered.emit(event);
      });
  }

  initPDFServices() {
    this.pdfLinkService = new PDFJSViewer.PDFLinkService({
      eventBus: this.eventBus,
      ...this.getPDFLinkServiceConfig()
    });
    this.pdfFindController = new PDFJSViewer.PDFFindController({
      eventBus: this.eventBus,
      linkService: this.pdfLinkService
    });
  }

  getPDFOptions() {
    return {
      eventBus: this.eventBus,
      container: this.element.nativeElement.querySelector('div'),
      removePageBorders: !this._showBorders,
      linkService: this.pdfLinkService,
      textLayerMode: this._renderText ? this._renderTextMode : RenderTextMode.DISABLED,
      findController: this.pdfFindController,
      l10n: new PDFJSViewer.GenericL10n('en'),
      imageResourcesPath: this._imageResourcesPath,
      annotationEditorMode: (PDFJS as any).AnnotationEditorType?.DISABLE ?? -1
    };
  }

  setupViewer() {
    if (this.pdfViewer) {
      this.pdfViewer.setDocument(null);
    }
    assign(PDFJS, 'disableTextLayer', !this._renderText);
    this.initPDFServices();
    if (this._showAll) {
      this.pdfViewer = new PDFJSViewer.PDFViewer(this.getPDFOptions() as any);
    } else {
      this.pdfViewer = new PDFJSViewer.PDFSinglePageViewer(this.getPDFOptions() as any);
    }
    this.pdfLinkService.setViewer(this.pdfViewer);
    (this.pdfViewer as any)._currentPageNumber = this._page;
  }

  getValidPageNumber(page: number) {
    if (!this._pdf) return 1;
    if (page < 1) {
      return 1;
    }
    if (page > this._pdf.numPages) {
      return this._pdf.numPages;
    }
    return page;
  }

  getDocumentParams() {
    const srcType = typeof this.src;

    const cloneBuffer = (val: any) => {
      if (!val) return val;
      if (val instanceof ArrayBuffer) {
        return val.slice(0);
      }
      if (ArrayBuffer.isView(val)) {
        const view = val as any;
        return new Uint8Array(view.buffer.slice(view.byteOffset, view.byteOffset + view.byteLength));
      }
      return val;
    };

    if (!this._cMapsUrl) {
      return cloneBuffer(this.src);
    }
    const params: any = {
      cMapUrl: this._cMapsUrl,
      cMapPacked: true,
      enableXfa: true
    };
    params.isEvalSupported = false; // Fix CVE-2024-4367
    if (srcType === 'string') {
      params.url = this.src;
    } else if (srcType === 'object' && this.src) {
      if ((this.src as any).byteLength !== undefined) {
        params.data = cloneBuffer(this.src);
      } else {
        Object.assign(params, this.src);
        if (params.data) {
          params.data = cloneBuffer(params.data);
        }
      }
    }
    return params;
  }

  loadPDF() {
    if (!this.src) {
      return;
    }
    if (this.lastLoaded === this.src) {
      this.update();
      return;
    }
    this.clear();
    this.setupViewer();
    this.loadingTask = getDocument(this.getDocumentParams());
    this.loadingTask.onProgress = (progressData) => {
      this.onProgress.emit(progressData);
    };
    const src = this.src;
    from(this.loadingTask.promise)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (pdf) => {
          this._pdf = pdf;
          this.lastLoaded = src;
          this.afterLoadComplete.emit(pdf);
          this.resetPdfDocument();
          this.update();
        },
        error: (error) => {
          this.lastLoaded = null;
          this.onError.emit(error);
        }
      });
  }

  update() {
    this.page = this._page;
    this.render();
  }

  render() {
    this._page = this.getValidPageNumber(this._page);
    if (this._rotation !== 0 || this.pdfViewer.pagesRotation !== this._rotation) {
      this.pdfViewer.firstPagePromise?.then(() => (this.pdfViewer.pagesRotation = this._rotation));
    }
    if (this._stickToPage) {
      setTimeout(() => {
        this.pdfViewer.currentPageNumber = this._page;
      });
    }
    if (!this.pdfViewer._pages?.length) {
      const sub = this.pageInitialized.subscribe(() => {
        this.updateSize();
        sub.unsubscribe();
      });
    } else {
      this.updateSize();
    }
  }

  getScale(viewportWidth: number, viewportHeight: number) {
    const borderSize = this._showBorders ? 2 * PdfViewerComponent.BORDER_WIDTH : 0;
    const pdfContainerWidth = this.pdfViewerContainer.nativeElement.clientWidth - borderSize;
    const pdfContainerHeight = this.pdfViewerContainer.nativeElement.clientHeight - borderSize;
    if (
      pdfContainerHeight === 0 ||
      viewportHeight === 0 ||
      pdfContainerWidth === 0 ||
      viewportWidth === 0
    ) {
      return 1;
    }
    let ratio = 1;
    switch (this._zoomScale) {
      case 'page-fit':
        ratio = Math.min(pdfContainerHeight / viewportHeight, pdfContainerWidth / viewportWidth);
        break;
      case 'page-height':
        ratio = pdfContainerHeight / viewportHeight;
        break;
      case 'page-width':
      default:
        ratio = pdfContainerWidth / viewportWidth;
        break;
    }
    return (this._zoom * ratio) / PdfViewerComponent.CSS_UNITS;
  }

  resetPdfDocument() {
    this.pdfLinkService.setDocument(this._pdf as any, null);
    this.pdfFindController.setDocument(this._pdf as any);
    this.pdfViewer.setDocument(this._pdf as any);
  }

  initialize() {
    if (isSSR() || !this.isVisible) {
      return;
    }
    this.isInitialized = true;
    this.initEventBus();
    this.setupViewer();
  }

  setupResizeListener() {
    if (isSSR()) {
      return;
    }
    this.ngZone.runOutsideAngular(() => {
      fromEvent(window, 'resize')
        .pipe(
          debounceTime(100),
          filter(() => this._canAutoResize && !!this._pdf),
          takeUntil(this.destroy$)
        )
        .subscribe(() => {
          this.updateSize();
        });
    });
  }
}
